// ─────────────────────────────────────────────────────────────────────────────
// DTL — Design Token Library
// Fuente: Figma DTL (rN7bP6QHjfVjmRusDdPisl)
// Archivo global — importar desde cualquier módulo.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Primitives ──────────────────────────────────────────────────────────────

export const color = {
  base: {
    black: '#000000',
    white: '#FFFFFF'
  },
  blue: {
    0:   '#E7F6FD80',
    50:  '#E7F6FD',
    100: '#CFEDFB',
    200: '#9FDBF6',
    300: '#6EC9F2',
    400: '#3EB7ED',
    500: '#0EA5E9',
    600: '#0B84BA',
    700: '#08638C',
    800: '#06425D',
    900: '#03212F'
  },
  fucsia: {
    0:   '#FDEDF580',
    50:  '#FDEDF5',
    100: '#FBDAEB',
    200: '#F7B6D6',
    300: '#F491C2',
    400: '#F06DAD',
    500: '#EC4899',
    600: '#BF3A7C',
    700: '#922C5E',
    800: '#651D41',
    900: '#380F23'
  },
  green: {
    0:   '#E9F9EF80',
    50:  '#E9F9EF',
    100: '#D3F3DF',
    200: '#A7E8BF',
    300: '#7ADC9E',
    400: '#4ED17E',
    500: '#22C55E',
    600: '#1B9E4B',
    700: '#147638',
    800: '#0E4F26',
    900: '#072713'
  },
  grey: {
    0:   '#E9E9EE80',
    50:  '#E9E9EE',
    100: '#D8DAE0',
    200: '#B8BAC6',
    300: '#979BAB',
    400: '#777B91',
    500: '#565C76',
    600: '#454A5E',
    700: '#343747',
    800: '#22252F',
    900: '#111218'
  },
  lime: {
    0:   '#F2FAE780',
    50:  '#F2FAE7',
    100: '#E6F6CE',
    200: '#CDEC9D',
    300: '#B3E36D',
    400: '#9AD93C',
    500: '#81D00B',
    600: '#67A609',
    700: '#4D7D07',
    800: '#345304',
    900: '#1A2A02'
  },
  orange: {
    0:   '#FFF1E580',
    50:  '#FFF1E5',
    100: '#FFC999',
    200: '#FFC999',  // nota: mismo valor que 100 en Figma
    300: '#FFAD66',
    400: '#FF9233',
    500: '#FF7700',
    600: '#CC5F00',
    700: '#994700',
    800: '#663000',
    900: '#663000'   // nota: mismo valor que 800 en Figma
  },
  purple: {
    0:   '#F2E7FF80',
    50:  '#F2E7FF',
    100: '#E4CEFF',
    200: '#CA9EFF',
    300: '#AF6DFF',
    400: '#953DFF',
    500: '#7A0CFF',
    600: '#620ACC',
    700: '#490799',
    800: '#310566',
    900: '#180233'
  },
  red: {
    0:   '#FDECEC80',
    50:  '#FDECEC',
    100: '#FCDADA',
    200: '#F9B4B4',
    300: '#F58F8F',
    400: '#F26969',
    500: '#EF4444',
    600: '#C13737',
    700: '#932929',
    800: '#661C1C',
    900: '#380E0E'
  },
  yellow: {
    0:   '#FFF9E580',
    50:  '#FFF9E5',
    100: '#FFF4CC',
    200: '#FFE999',
    300: '#FFDE66',
    400: '#FFD333',
    500: '#FFC800',
    600: '#CCA000',
    700: '#997800',
    800: '#665000',
    900: '#332800'
  }
} as const;

// ─── Semantic tokens ─────────────────────────────────────────────────────────

export const semantic = {
  background: {
    accent:    color.purple[0],
    error:     color.red[50],
    info:      color.blue[0],
    primary:   color.base.white,
    secondary: color.grey[0],
    success:   color.green[0],
    warning:   color.orange[0]
  },
  border: {
    accent:    color.purple[200],
    error:     color.red[300],
    info:      color.blue[300],
    primary:   color.grey[500],
    secondary: color.grey[200],
    success:   color.green[300],
    warning:   color.orange[300]
  },
  text: {
    accent:    { default: color.purple[500], disabled: color.purple[300] },
    error:     { default: color.red[600],    disabled: color.red[300]    },
    link:      { default: color.blue[600],   disabled: color.blue[300]   },
    primary:   { default: color.grey[800],   disabled: color.grey[400]   },
    secondary: { default: color.grey[500],   disabled: color.grey[300]   },
    success:   { default: color.green[600],  disabled: color.green[300]  },
    warning:   { default: color.orange[600], disabled: color.orange[300] }
  },
  icon: {
    accent:    { default: color.purple[500], disabled: color.purple[300] },
    error:     { default: color.red[600],    disabled: color.red[300]    },
    link:      { default: color.blue[600],   disabled: color.blue[300]   },
    primary:   { default: color.grey[800],   disabled: color.grey[400]   },
    secondary: { default: color.grey[500],   disabled: color.grey[500]   },
    success:   { default: color.green[600],  disabled: color.green[300]  },
    warning:   { default: color.orange[600], disabled: color.orange[300] }
  }
} as const;
